import { useState, useEffect, useCallback } from 'react';
import { Mail, Cake, UserX } from 'lucide-react';
import { palette, admin, transition } from '../../styles/tokens';
import { supabase } from '../../lib/supabase';
import { Toast } from '../common/Toast';

const CARD: React.CSSProperties = { background: palette.white, borderRadius: admin.cardRadius, border: `1px solid ${admin.border}`, overflow: 'hidden' };
const CARD_HEAD: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderBottom: `1px solid ${palette.g100}` };
const CARD_BODY: React.CSSProperties = { padding: '16px 20px' };
const LABEL: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: palette.g500, marginBottom: 4, display: 'block', textTransform: 'uppercase' as const, letterSpacing: '0.04em' };
const INPUT: React.CSSProperties = { width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${palette.g200}`, fontSize: 13.5, color: palette.g800, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' as const };

interface Automation {
  id?: string;
  type: string;
  enabled: boolean;
  days_threshold: number;
  subject: string;
  body: string;
  include_booking_link: boolean;
  include_intro_offer: boolean;
}

const DEFAULTS: Record<string, Automation> = {
  visit_recency: {
    type: 'visit_recency', enabled: false, days_threshold: 30,
    subject: 'We miss you, {name}!',
    body: "It's been a while since your last visit. We'd love to see you again.",
    include_booking_link: true, include_intro_offer: false,
  },
  birthday: {
    type: 'birthday', enabled: false, days_threshold: 0,
    subject: 'Happy birthday, {name}!',
    body: 'Wishing you a wonderful birthday! Here\'s a little something from us.',
    include_booking_link: true, include_intro_offer: false,
  },
  no_show_followup: {
    type: 'no_show_followup', enabled: false, days_threshold: 1,
    subject: 'We missed you today',
    body: "We noticed you couldn't make it. No worries — we'd love to rebook when you're ready.",
    include_booking_link: true, include_intro_offer: false,
  },
};

interface Props {
  businessId: string | undefined;
  tier: string;
}

export function MarketingSection({ businessId, tier }: Props) {
  const [automations, setAutomations] = useState<Record<string, Automation>>({});
  const [sendCounts, setSendCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);

  const canUse = tier === 'business' || tier === 'trial';

  useEffect(() => {
    if (!businessId) return;
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- pre-existing; IIFE in effect
    (async () => {
      const { data } = await supabase.from('marketing_automations').select('*').eq('business_id', businessId);
      const map: Record<string, Automation> = {};
      for (const row of data ?? []) map[row.type] = row;
      setAutomations(map);

      const { data: sends } = await supabase.from('marketing_sends').select('type').eq('business_id', businessId);
      const counts: Record<string, number> = {};
      for (const s of sends ?? []) counts[s.type] = (counts[s.type] ?? 0) + 1;
      setSendCounts(counts);
      setLoading(false);
    })();
  }, [businessId]);

  const saveAutomation = useCallback(async (type: string, updates: Partial<Automation>) => {
    if (!businessId) return;
    const current = automations[type] ?? DEFAULTS[type];
    const merged = { ...current, ...updates };

    if (current.id) {
      await supabase.from('marketing_automations').update({
        enabled: merged.enabled, days_threshold: merged.days_threshold,
        subject: merged.subject, body: merged.body,
        include_booking_link: merged.include_booking_link,
        include_intro_offer: merged.include_intro_offer,
        updated_at: new Date().toISOString(),
      }).eq('id', current.id);
    } else {
      const { data } = await supabase.from('marketing_automations').insert({
        business_id: businessId, type: merged.type,
        enabled: merged.enabled, days_threshold: merged.days_threshold,
        subject: merged.subject, body: merged.body,
        include_booking_link: merged.include_booking_link,
        include_intro_offer: merged.include_intro_offer,
      }).select('id').maybeSingle();
      if (data) merged.id = data.id;
    }
    setAutomations(prev => ({ ...prev, [type]: merged }));
    setSaved(true); setTimeout(() => setSaved(false), 2000);
  }, [businessId, automations]);

  if (loading) return null;

  if (!canUse) {
    return (
      <div style={CARD}>
        <div style={CARD_HEAD}>
          <span style={{ fontSize: 14, fontWeight: 600, color: palette.g800 }}>Automated Campaigns</span>
        </div>
        <div style={{ ...CARD_BODY, textAlign: 'center', padding: '32px 20px' }}>
          <div style={{ fontSize: 14, color: palette.g500, marginBottom: 8 }}>Automated email campaigns are available on the Business plan.</div>
          <div style={{ fontSize: 13, color: palette.accent, fontWeight: 600 }}>Upgrade to Business</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Toast message="Campaign saved." type="success" isVisible={saved} onDismiss={() => setSaved(false)} />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: palette.g800 }}>Automated Campaigns</div>
          <div style={{ fontSize: 13, color: palette.g500, marginTop: 2 }}>Send emails automatically based on client activity.</div>
        </div>
      </div>

      <AutomationCard
        icon={<Mail size={16} color={palette.accent} />}
        title="We miss you"
        description="Email clients who haven't visited recently"
        auto={automations.visit_recency ?? DEFAULTS.visit_recency}
        sends={sendCounts.visit_recency ?? 0}
        showDays
        onSave={u => saveAutomation('visit_recency', u)}
      />
      <AutomationCard
        icon={<Cake size={16} color="#F59E0B" />}
        title="Happy birthday"
        description="Birthday greetings with an optional offer"
        auto={automations.birthday ?? DEFAULTS.birthday}
        sends={sendCounts.birthday ?? 0}
        note="Requires client birthdays — add them in the Clients page"
        onSave={u => saveAutomation('birthday', u)}
      />
      <AutomationCard
        icon={<UserX size={16} color={palette.red} />}
        title="Missed appointment"
        description="Gentle follow-up after a no-show"
        auto={automations.no_show_followup ?? DEFAULTS.no_show_followup}
        sends={sendCounts.no_show_followup ?? 0}
        onSave={u => saveAutomation('no_show_followup', u)}
      />
    </div>
  );
}

function AutomationCard({ icon, title, description, auto, sends, showDays, note, onSave }: {
  icon: React.ReactNode; title: string; description: string;
  auto: Automation; sends: number; showDays?: boolean; note?: string;
  onSave: (u: Partial<Automation>) => void;
}) {
  return (
    <div style={CARD}>
      <div style={CARD_HEAD}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {icon}
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: palette.g800 }}>{title}</div>
            <div style={{ fontSize: 12, color: palette.g400 }}>{description}</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {sends > 0 && <span style={{ fontSize: 11, color: palette.g400 }}>{sends} sent</span>}
          <button onClick={() => onSave({ enabled: !auto.enabled })} style={{
            width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
            background: auto.enabled ? palette.accent : palette.g200, position: 'relative',
            transition: `background ${transition.fast}`, flexShrink: 0,
          }}>
            <div style={{
              width: 20, height: 20, borderRadius: 10, background: '#fff', position: 'absolute', top: 2,
              left: auto.enabled ? 22 : 2, transition: `left ${transition.fast}`,
              boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
            }} />
          </button>
        </div>
      </div>
      {auto.enabled && (
        <div style={CARD_BODY}>
          {showDays && (
            <div style={{ marginBottom: 12 }}>
              <label style={LABEL}>Send after</label>
              <div style={{ display: 'flex', gap: 6 }}>
                {[30, 60, 90].map(d => (
                  <button key={d} onClick={() => onSave({ days_threshold: d })} style={{
                    padding: '5px 14px', borderRadius: 6, fontSize: 12, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer',
                    border: auto.days_threshold === d ? `2px solid ${palette.accent}` : `1px solid ${palette.g200}`,
                    background: auto.days_threshold === d ? palette.accentWash : palette.white,
                    color: auto.days_threshold === d ? palette.accentDeep : palette.g600,
                  }}>{d} days</button>
                ))}
              </div>
            </div>
          )}
          <div style={{ marginBottom: 12 }}>
            <label style={LABEL}>Subject line</label>
            <input style={INPUT} value={auto.subject} onChange={e => onSave({ subject: e.target.value })} placeholder="Subject..." />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={LABEL}>Message body</label>
            <textarea style={{ ...INPUT, minHeight: 60, resize: 'vertical' }} value={auto.body} onChange={e => onSave({ body: e.target.value })} />
          </div>
          <div style={{ display: 'flex', gap: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: palette.g600, cursor: 'pointer' }}>
              <input type="checkbox" checked={auto.include_booking_link} onChange={e => onSave({ include_booking_link: e.target.checked })} style={{ accentColor: palette.accent }} />
              Include booking link
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: palette.g600, cursor: 'pointer' }}>
              <input type="checkbox" checked={auto.include_intro_offer} onChange={e => onSave({ include_intro_offer: e.target.checked })} style={{ accentColor: palette.accent }} />
              Mention intro offers
            </label>
          </div>
          {note && <div style={{ fontSize: 11, color: palette.g400, marginTop: 10, fontStyle: 'italic' }}>{note}</div>}
          <div style={{ fontSize: 11, color: palette.g400, marginTop: 10 }}>
            Use {'{name}'} in subject or body to insert the client's first name.
          </div>
        </div>
      )}
    </div>
  );
}
